import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import useDocumentDelete from "./useDocumentDelete";
import useDocumentMetadata from "./useDocumentMetadata";
import useDocumentUpload from "./useDocumentUpload";

/**
 * Type definition for document form values
 */
export type DocumentFormValues = {
  documents: DocumentData[];
};

/**
 * Orchestrates document management operations (upload, delete, metadata update)
 * by coordinating specialized hooks for each operation type.
 *
 * @param caseId - Case ID for document operations
 * @param profileId - Profile ID for document association
 * @returns { processDocuments, loading } - Function for processing documents and loading state
 */
export default function useDocumentProcessor(caseId: number, profileId: number) {
  const [isProcessing, setIsProcessing] = useState(false);

  // Use specialized hooks for document operations
  const { deleteDocuments, loading: deleteLoading } = useDocumentDelete(caseId);
  const { uploadDocuments, loading: uploadLoading } = useDocumentUpload(caseId);
  const { updateDocumentMetadata, loading: updateLoading } = useDocumentMetadata(
    caseId,
    profileId,
  );

  /**
   * Identifies which files need to be replaced and which are new uploads
   */
  const prepareFilesToProcess = useCallback(
    (
      newSelectedFiles: DocumentFormValues,
      initialFiles: DocumentData[],
    ): { replacedFiles: DocumentData[]; newUploadFiles: DocumentData[] } => {
      const replacedFiles: DocumentData[] = [];
      const newUploadFiles: DocumentData[] = [];

      newSelectedFiles?.documents?.forEach((document, index) => {
        const originalFile = initialFiles[index].file;
        const newFile = document.file;

        // Using the lastModified attribute to check whether the file has been added or changed
        // The initial file from cloud doesn't have lastModified attribute
        const isNewFile = newFile && !!newFile.file?.lastModified;

        // If we have an original file and it was replaced with a new file or removed
        if (originalFile && (!newFile || isNewFile)) {
          replacedFiles.push({
            ...initialFiles[index],
            file: originalFile,
          });
        }

        // If we have a new file (has lastModified attribute)
        if (isNewFile) {
          newUploadFiles.push({
            ...initialFiles[index],
            file: newFile,
          });
        }
      });

      return { replacedFiles, newUploadFiles };
    },
    [],
  );

  /**
   * Main orchestration function that:
   * 1. Identifies files to delete and upload
   * 2. Deletes replaced documents
   * 3. Uploads new documents
   * 4. Updates document metadata
   *
   * @param newSelectedFiles - Form values with current document selections
   * @param initialFiles - Original documents for comparison
   * @returns Promise resolving to updated documents
   */
  const processDocuments = useCallback(
    async (newSelectedFiles: DocumentFormValues, initialFiles: DocumentData[]) => {
      setIsProcessing(true);
      try {
        const { replacedFiles, newUploadFiles } = prepareFilesToProcess(
          newSelectedFiles,
          initialFiles,
        );

        // First delete documents that have been replaced
        await deleteDocuments(replacedFiles);

        // Then upload and register new documents
        const updatedDocuments = await uploadDocuments(newUploadFiles);

        // Finally update document metadata
        await updateDocumentMetadata(updatedDocuments);

        return updatedDocuments;
      } finally {
        setIsProcessing(false);
      }
    },
    [prepareFilesToProcess, deleteDocuments, uploadDocuments, updateDocumentMetadata],
  );

  return {
    processDocuments,
    loading: isProcessing || deleteLoading || uploadLoading || updateLoading,
  };
}
